/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IDistributorInfo } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';
import classnames from 'classnames';
import { ITermsOfServiceLinkData } from '../distributor-selector.props.autogenerated';

export interface IDistributorSelectorFoundResultsMessage {
    distributorCountMessage: string;
    distributors?: IDistributorInfo[];
}

export interface IDistributorSelectorTermsOfServiceProps {
    link?: ITermsOfServiceLinkData;
    requestContext: Msdyn365.IRequestContext;
    onTextChange?(event: Msdyn365.ContentEditableEvent): void;
}

export interface IDistributorDoNotShowProps {
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    doNotShowAgainMessage: string;
}

export interface IDistributorButtonProps {
    onClick(event: React.MouseEvent<HTMLElement>): void;
    text: string;
    className: string;
}

export interface IDistributorEmptyMessageProps {
    emptyLocationsText?: string;
}

export const DistributorSelectorCountMessage: React.FC<IDistributorSelectorFoundResultsMessage> = ({
    distributors,
    distributorCountMessage
}) => {
    if (distributors) {
        return (
            <div className='ms-distributor-selector__count-message'>
                {distributorCountMessage.replace('{count}', distributors.length.toString())}
            </div>
        );
    }

    return null;
};

export const DistributorSelectorTermsOfService: React.FC<IDistributorSelectorTermsOfServiceProps> = ({
    link,
    requestContext,
    onTextChange
}) => {
    if (link) {
        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: link.ariaLabel,
            className: 'ms-distributor-selector__terms-link',
            linkText: link.linkText,
            linkUrl: link.linkUrl.destinationUrl,
            openInNewTab: link.openInNewTab,
            role: 'link'
        };

        return (
            <div className='ms-distributor-selector__footer'>
                <Msdyn365.Link link={editableLink} editProps={{ onTextChange, requestContext }} />
            </div>
        );
    }
    return null;
};

export const DistributorSelectorWaiting: React.FC = () => {
    return <div className={classnames('msc-waiting', 'ms-distributor-selector__search-in-progress')} />;
};

export const DistributorSelectorDoNotShowAgain: React.FC<IDistributorDoNotShowProps> = ({ onChange, doNotShowAgainMessage }) => {
    return (
        <div className='ms-distributor-selector__do-not-show-again'>
            <input type='checkbox' onChange={onChange}></input>
            <span>{doNotShowAgainMessage}</span>
        </div>
    );
};

export const DistributorSelectorButton: React.FC<IDistributorButtonProps> = ({ onClick, text, className }) => {
    return (
        <button className={className} onClick={onClick}>
            {text}
        </button>
    );
};

export const DistributorEmptyMessage: React.FC<IDistributorEmptyMessageProps> = ({ emptyLocationsText }) => (
    <div className='ms-distributor-selector__no-distributors'>
        <p>{emptyLocationsText}</p>
    </div>
);
